import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useActionData } from 'react-router-dom'

import { StyledActionButton } from '@/components/Buttons.tsx'
import { Datepicker } from '@/components/Datepicker.tsx'
import { Label } from '@/components/Label.tsx'
import { Radio, RadioGroup } from '@/components/Radio.tsx'
import {
  FormElement,
  FormElementError,
  FormStyled,
  FormSubmit,
} from '@/components/templates/Form.tsx'
import { FormField } from '@/components/templates/FormField'
import { ScrollableContent } from '@/components/templates/ScreenLayout.tsx'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper.tsx'
import { Description, Header2 } from '@/components/Text.tsx'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress.ts'
import { ActionData } from '@/screens/velthuis/PatientDetails/actions.ts'
import { patientGenderOptions } from '@/screens/velthuis/PatientDetails/utils.ts'
import { useAppointmentStore } from '@/store.ts'
import { VelthuisFormStep } from '@/utils/VelthuisFormSteps.ts'

const PatientDetails = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'patientDetails',
  })
  const { t: tGeneral } = useTranslation('translation', {
    keyPrefix: 'fields',
  })

  const {
    patientData: {
      dateOfBirth,
      email,
      firstName,
      gender,
      houseNumber,
      initials,
      lastName,
      phone,
      postalCode,
    } = {},
  } = useAppointmentStore.getState()

  const { trackFormError, trackFormInteraction } = useTrackFormProgress(
    VelthuisFormStep.PatientDetails,
  )
  const actionData = useActionData() as ActionData | undefined

  useEffect(() => {
    const error = actionData?.errors
      ? actionData.errors.map((e) => e.message).join(',')
      : actionData?.error
    if (error) {
      trackFormError({
        error,
      })
    }
  }, [actionData?.errors, actionData?.error])

  return (
    <ScreenWrapper>
      <FormStyled method="post">
        <Header2>{t('header')}</Header2>
        <Description>{t('description')}</Description>

        <ScrollableContent>
          <FormField
            fieldName="initials"
            fieldType="text"
            defaultValue={initials}
            trackFormInteraction={trackFormInteraction}
            allErrors={actionData?.errors}
          />
          <FormField
            fieldName="firstName"
            fieldType="text"
            defaultValue={firstName}
            trackFormInteraction={trackFormInteraction}
            allErrors={actionData?.errors}
          />
          <FormField
            fieldName="lastName"
            fieldType="text"
            defaultValue={lastName}
            trackFormInteraction={trackFormInteraction}
            allErrors={actionData?.errors}
          />
          <FormElement>
            <Label htmlFor="gender">{tGeneral('gender.label')}</Label>
            <RadioGroup
              name="gender"
              defaultValue={gender}
              onFocus={() => {
                trackFormInteraction({
                  action: 'click',
                  formFieldName: 'gender',
                })
              }}
            >
              {patientGenderOptions.map((option) => (
                <Radio
                  key={option}
                  value={option}
                  text={tGeneral(`gender.options.${option}`)}
                />
              ))}
            </RadioGroup>
            <FormElementError>
              {actionData?.errors?.find((e) => e.path[0] === 'gender')
                ? tGeneral('gender.error')
                : undefined}
            </FormElementError>
          </FormElement>
          <FormElement>
            <Datepicker
              name="dateOfBirth"
              placeholder={tGeneral('dateOfBirth.placeholder')}
              label={tGeneral('dateOfBirth.label')}
              errorMessage={
                actionData?.errors?.find((e) => e.path[0] === 'dateOfBirth')
                  ? tGeneral('dateOfBirth.error')
                  : undefined
              }
              defaultValue={dateOfBirth}
              onChange={(value) => {
                if (value) {
                  trackFormInteraction({
                    action: 'click',
                    formFieldName: 'dateOfBirth',
                  })
                }
              }}
            />
          </FormElement>
          <FormField
            fieldName="email"
            fieldType="email"
            defaultValue={email}
            trackFormInteraction={trackFormInteraction}
            allErrors={actionData?.errors}
          />
          <FormField
            fieldName="phone"
            fieldType="tel"
            defaultValue={phone}
            trackFormInteraction={trackFormInteraction}
            allErrors={actionData?.errors}
          />
          {/* Postcode + house number are always collected so clinic staff can
              verify a caller's identity by phone. */}
          <FormField
            fieldName="postalCode"
            fieldType="text"
            defaultValue={postalCode}
            trackFormInteraction={trackFormInteraction}
            allErrors={actionData?.errors}
          />
          <FormField
            fieldName="houseNumber"
            fieldType="text"
            defaultValue={houseNumber}
            trackFormInteraction={trackFormInteraction}
            allErrors={actionData?.errors}
          />
        </ScrollableContent>

        <FormSubmit>
          <StyledActionButton variant="Primary" type="submit">
            {t('actions.continue')}
          </StyledActionButton>
        </FormSubmit>
      </FormStyled>
    </ScreenWrapper>
  )
}

export { PatientDetails }
